import { assert } from '@ember/debug';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';
import type { FormData, FormKey } from 'ember-headless-form';
import type { HeadlessFormComponentSignature } from 'ember-headless-form/components/headless-form';
import { type UserData } from '../../../types.ts';
import FieldsetRow from '../fieldset-row.gts';
import type RadioField628 from './radio';
import RadioField from './radio.gts';

export interface WrFormRadioGroupFieldComponentSignature<
  DATA extends UserData,
  KEY extends FormKey<FormData<DATA>> = FormKey<FormData<DATA>>
> {
  Element: HTMLFieldSetElement;
  Args: {
    label: string;
    name: KEY;
    form: HeadlessFormComponentSignature<DATA, unknown>['Blocks']['default'][0];
    onClick?: () => void;
    variant?: 'grouped-buttons';
  };
  Blocks: {
    default: [
      Radio: WithBoundArgs<
        typeof RadioField628,
        'name' | 'selectedValue' | 'onChange'
      >
    ];
    col2?: [
      Radio: WithBoundArgs<
        typeof RadioField628,
        'name' | 'selectedValue' | 'onChange'
      >
    ];
  };
}

export default class WrFormRadioGroupFieldComponent<
  DATA extends UserData,
  KEY extends FormKey<FormData<DATA>> = FormKey<FormData<DATA>>
> extends Component<WrFormRadioGroupFieldComponentSignature<DATA, KEY>> {
  formatValue = (value: unknown) => {
    assert(
      `Only string or boolean values are expected for ${
        this.args.name
      }, but you passed ${typeof value}`,
      value == null || typeof value === 'string' || typeof value === 'boolean'
    );

    return value;
  };

  setValue = (fieldSetValue: (value: DATA[KEY]) => void) => {
    return (value: string) => {
      if (value == 'true') {
        fieldSetValue(true as DATA[KEY]);
      } else if (value == 'false') {
        fieldSetValue(false as DATA[KEY]);
      } else {
        fieldSetValue(value as DATA[KEY]);
      }
      this.args.onClick?.();
    };
  };

  get radioVariant() {
    return this.args.variant == 'grouped-buttons' ? 'button' : undefined;
  }

  <template>
    {{! prettier-ignore }}
    <@form.Field @name={{@name}} as |field|>
{{! @glint-expect-error }}
      {{#let (component RadioField variant=this.radioVariant name=@name selectedValue=(this.formatValue field.value) onChange=(this.setValue field.setValue) ) as |Radio| }}
        <FieldsetRow
          @legend={{@label}}
          role='radiogroup'
          aria-describedby={{field.errorId}}
          aria-invalid={{if field.isInvalid 'true'}}
          aria-required='true'
          class='gap-2 [&>:nth-child(2)]:flex [&>:nth-child(2)]:flex-col [&>:nth-child(2)]:gap-2 [&>:nth-child(2)]:sm:flex-row'
          ...attributes
        >
          <:col1>
            {{yield Radio}}
          </:col1>
          <:col2>
            {{yield Radio to='col2'}}
          </:col2>
        </FieldsetRow>
      {{/let}}
    </@form.Field>
  </template>
}
